/**
 * The missions as the screens need them (03 §2, 10 §2): number, native terrain, base threat, category and the
 * FACTS the briefing, the loadout screen and the cards are driven by. Read from the roguelike layer's own
 * tables (src/meta/content) rather than mirrored, so a screen can never disagree with the campaign about what
 * a mission is. Names and one-line texts still come from `GameApi.describe`. Classic lists the 1987 twelve
 * (`CLASSIC_ARCHETYPES`); Practice and Records list all thirty-six, grouped (`MISSION_GROUPS`).
 */

import type { ArchetypeFacts, MissionCategory, MissionTag } from '../../meta/content/archetypeFacts';
import { missionTags, tagLabel } from '../../meta/content/archetypeFacts';
import type { MissionGroup } from '../../meta/content/archetypes';
import { ARCHETYPES as TABLE, groupOf, MISSION_GROUPS } from '../../meta/content/archetypes';
import { isOriginalArchetype } from '../../sim/types';
import type { ArchetypeId, Biome, MissionParams, ModifierId, TimeOfDay, WeatherKind } from '../../sim/types';

export type { ArchetypeFacts, MissionCategory, MissionGroup, MissionTag };
export { MISSION_GROUPS };

export interface ArchetypeInfo {
  id: ArchetypeId;
  /** 1-12: the manual's order. 13-36: the expansion's (10 §2). */
  number: number;
  biome: Biome;
  baseThreat: number;
  category: MissionCategory;
  /** Where the pickers and the records table file it: `1987` for the original twelve, else its category. */
  group: MissionGroup;
  facts: ArchetypeFacts;
}

export const ARCHETYPES: readonly ArchetypeInfo[] = TABLE.map((a) => ({
  id: a.id, number: a.number, biome: a.native, baseThreat: a.baseThreat, category: a.facts.category,
  group: groupOf(a), facts: a.facts,
}));

/** Classic mode is the 1987 game (04 §1, 10 intro): the original twelve and nothing else. */
export const CLASSIC_ARCHETYPES: readonly ArchetypeInfo[] = ARCHETYPES.filter((a) => isOriginalArchetype(a.id));

export const BIOMES: readonly Biome[] = ['arctic', 'desert', 'temperate'];
export const TIMES_OF_DAY: readonly TimeOfDay[] = ['dawn', 'day', 'dusk', 'night'];

/**
 * ROUND5: temperate ends in `thunderstorm`, as arctic ends in `blizzard` and the desert in
 * `sandstorm`. Each list ends with its biome's storm - the one weather Practice could not offer a
 * temperate map before, because temperate's storm was the `rain` already two lines above it.
 */
const WEATHER_BY_BIOME: Readonly<Record<Biome, readonly WeatherKind[]>> = {
  arctic: ['clear', 'snow', 'blizzard'],
  desert: ['clear', 'sandstorm'],
  temperate: ['clear', 'rain', 'thunderstorm'],
};

/** The weather a terrain can have; the first entry is always `clear`. */
export function weathersFor(biome: Biome): readonly WeatherKind[] {
  return WEATHER_BY_BIOME[biome];
}

export function archetypeInfo(id: ArchetypeId): ArchetypeInfo {
  const info = ARCHETYPES.find((a) => a.id === id);
  if (info === undefined) throw new Error(`unknown archetype ${id}`);
  return info;
}

/** The manual's mission number, two digits: what a theatre-map node shows. */
export function archetypeNumber(id: ArchetypeId): string {
  const n = archetypeInfo(id).number;
  return n < 10 ? `0${n}` : String(n);
}

/** The missions of each group, in mission-number order; groups in the order the pickers show them. */
export function groupedArchetypes(): Array<{ group: MissionGroup; missions: ArchetypeInfo[] }> {
  return MISSION_GROUPS.map((group) => ({ group, missions: ARCHETYPES.filter((a) => a.group === group) }));
}

/** The small tags a mission's card carries (TIMED, NO DROP, POD A, NO RECALL, QUIET): id and label. */
export function archetypeTags(id: ArchetypeId): Array<{ tag: MissionTag; label: string }> {
  const { facts } = archetypeInfo(id);
  return missionTags(facts).map((tag) => ({ tag, label: tagLabel(tag, facts) }));
}

export function clampThreat(threat: number): number {
  return Math.min(9, Math.max(1, Math.round(threat)));
}

/** A positive 31-bit seed from the wall clock; menus may use it, the pure layers may not. */
export function freshSeed(): number {
  return (Math.floor(Math.random() * 0x7fffffff) ^ (Date.now() & 0x7fffffff)) >>> 0 || 1;
}

/** Classic mode (04 §1): native terrain, clear day, threat = base + the difficulty slider. */
export function classicParams(id: ArchetypeId, difficulty: number, seed: number): MissionParams {
  const info = archetypeInfo(id);
  const modifiers: ModifierId[] = [];
  return {
    archetype: id,
    biome: info.biome,
    timeOfDay: 'day',
    weather: 'clear',
    threat: clampThreat(info.baseThreat + difficulty),
    seed,
    modifiers,
    intel: [],
  };
}
